import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import routes from "constants/routes";
import { mapCouponToFormValues } from "utils/couponUtils";
import {
  buildEditCouponPayload,
  mapCouponDetailsFromApi,
} from "helpers/couponApi";
import { formatApiErrorForDisplay } from "helpers/packageManagementApi";
import { useCouponDetails, useEditCoupon } from "store/backend/apiHooks";
import { couponQueryKeys } from "store/backend/couponQueryKeys";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import { Loader } from "widgets";

import CouponForm from "./CouponForm";
import styles from "./CouponsList.module.scss";

const EditCoupon = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbarContext();

  const {
    data: couponDetails,
    isLoading,
    isError,
    error,
  } = useCouponDetails(id);
  const { mutateAsync, isPending } = useEditCoupon();

  if (isLoading) {
    return (
      <div className={styles.emptyState}>
        <Loader />
      </div>
    );
  }

  if (isError || !couponDetails) {
    return (
      <div className={styles.emptyState}>
        <p>{formatApiErrorForDisplay(error, "Failed to load coupon details.")}</p>
        <button
          type="button"
          className={styles.createButton}
          onClick={() => navigate(routes.coupons.list)}
        >
          Back to Coupons
        </button>
      </div>
    );
  }

  const handleSubmit = async (values) => {
    try {
      const payload = buildEditCouponPayload(values, couponDetails);
      await mutateAsync({ couponCode: values.code, payload });
      await queryClient.refetchQueries({
        queryKey: couponQueryKeys.detail(values.code),
      });
      openSnackbar(`Coupon "${values.code}" updated successfully`, "success");
      navigate(routes.coupons.view(values.code), { replace: true });
    } catch (submitError) {
      openSnackbar(
        formatApiErrorForDisplay(submitError, "Failed to update coupon."),
        "error"
      );
    }
  };

  const initialValues = mapCouponToFormValues(
    mapCouponDetailsFromApi(couponDetails)
  );

  return (
    <CouponForm
      key={id}
      mode="edit"
      initialValues={initialValues}
      onSubmit={handleSubmit}
      isSubmitting={isPending}
    />
  );
};

export default EditCoupon;
